import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
	ApiError,
	CollectionNames,
	Invoice,
	InvoiceCategoriesEnum,
	InvoiceOptional,
	InvoiceUpdate,
	MappingFieldType,
	ReportAPIResponse,
  SQLItem
} from 'src/modules/generated-api-clients/no-sql';
import { useApiNoSqlContext } from 'src/modules/contexts/ApiNoSqlContext';
import { useAuthenticator } from '@aws-amplify/ui-react';

import { logApiError, logAxiosError, logError, logRequest } from './helpers';
import { MUTATION_KEYS, QUERY_KEYS } from './enums';
import { AxiosError } from 'axios';

interface ReadSingleInvoiceParams {
	id?: string;
	managerId?: string;
	utilityId?: string;
	customerId?: string;
	createdDate?: string;
	fileName?: string;
	invoiceUrl?: string;
  isValidated?: boolean;
	project?: string;
}
interface ReadAllInvoicesParams extends ReadSingleInvoiceParams {
	skip?: number;
	limit?: number;
}
interface ReadUniqueFieldOr404 {
	collection: CollectionNames;
	unique?: string;
	id?: string;
	managerId?: string;
	documentId?: string;
	utilityId?: string;
	customerId?: string;
	updatedDate?: string;
	sqlId?: string,
	invoiceCompanyName?: string,
	createdDate?: string;
	fileName?: string;
	invoiceUrl?: string;
  isValidated?: boolean;
	commodity?: string;
	facilityName?: string;
	meterStartDate?: string;
	meterEndDate?: string;
	accountNumber?: string;
	facilityType?: string;
	question?: string;
	entity?: string;
	returnType?: MappingFieldType;
	isActive?: boolean;
	version?: number;
	invoiceCategory?: InvoiceCategoriesEnum;
	utilityType?: string;
}
interface UpdateInvoiceParams {
	id: string;
	requestBody: InvoiceUpdate;
}

/**
 * Custom hook for fetching all invoices.
 * Endpoint: GET '/api/v2/read/list/invoice'
 * @param params - An object containing the following properties:
 *   - id: The ID of the invoice (optional).
 *   - utilityId: The utility ID (optional).
 *   - customerId: The customer ID (optional).
 *   - createdDate: The created date (optional).
 *   - fileName: The file name (optional).
 *   - docPredPath: The document predicate path (optional).
 *   - invoiceUrl: The document URL (optional).
 *   - skip: The number of records to skip (optional).
 *   - limit: The maximum number of records to return (optional).
 * @returns Invoice[] or an error of type ApiError.
 */
export const useListInvoiceNoSql = (params?: ReadAllInvoicesParams) => {
	const { client } = useApiNoSqlContext();
	const { user } = useAuthenticator(context => [context.user]);
	const queryKey =
		params === undefined
			? [QUERY_KEYS.invoices.AllInvoices]
			: [QUERY_KEYS.invoices.AllInvoices, params];

	const query = async () => {
		try {
			if (!client) {
				throw new Error('API client is not initialized');
			}
			if (user.attributes === undefined) {
				throw new Error('AWS Amplify user attributes cannot be undefined');
			}
			const response =
				params === undefined
					? await client.invoice.invoiceListInvoiceOr404()
					: await client.invoice.invoiceListInvoiceOr404(
						params.id,
						params.managerId,
						params.utilityId,
						params.customerId,
						params.createdDate,
						params.fileName,
						params.invoiceUrl,
						params.isValidated,
						params.skip,
						params.limit
					);
			return response;
		} catch (error: unknown) {
			if (error instanceof ApiError) {
				logApiError(error);
			} else if (error instanceof AxiosError) {
				logAxiosError(error);
			} else {
				logError(error, 'Error fetching all invoices');
			}
			throw error;
		}
	};
	// TODO: this should return InvoiceOptional[] | []
	// should return an empty list instead
	return useQuery<Invoice[], ApiError>(queryKey, query);
};

/**
 * Custom hook for fetching all invoices.
 * Endpoint: GET '/api/v2/read/list/invoice-optional'
 * @param params - An object containing the following properties:
 *   - id: The ID of the invoice (optional).
 *   - utilityId: The utility ID (optional).
 *   - customerId: The customer ID (optional).
 *   - createdDate: The created date (optional).
 *   - fileName: The file name (optional).
 *   - docPredPath: The document predicate path (optional).
 *   - invoiceUrl: The document URL (optional).
 *   - project: Query param string with comma-separated field names as they appear in the database (optional).
 *   - skip: The number of records to skip (optional).
 *   - limit: The maximum number of records to return (optional).
 * @returns InvoiceOptional[] or an error of type ApiError.
 */
export const useListInvoiceOptionalNoSql = (params?: ReadAllInvoicesParams) => {
	const { client } = useApiNoSqlContext();
	const { user } = useAuthenticator(context => [context.user]);
	// const token = user.getSignInUserSession()?.getIdToken().getJwtToken();
	// if (client !== undefined && client !== null && token !== undefined) {
	// 	client.request.config.HEADERS = {"id_token": token}
	// } else {
	// 	throw new Error('API token error');
	// }
	const queryKey =
		params === undefined
			? [QUERY_KEYS.invoices.AllInvoices]
			: [QUERY_KEYS.invoices.AllInvoices, params];

	const query = async () => {
		try {
			if (!client) {
				throw new Error('API client is not initialized');
			}
			if (user.attributes === undefined) {
				throw new Error('AWS Amplify user attributes cannot be undefined');
			}
			const response =
				params === undefined
					? await client.invoice.invoiceListInvoiceOptionalOr404()
					: await client.invoice.invoiceListInvoiceOptionalOr404(
						params.id,
						params.managerId,
						params.utilityId,
						params.customerId,
						params.createdDate,
						params.fileName,
						params.invoiceUrl,
						params.isValidated,
						params.project,
						params.skip,
						params.limit
					);
			// logRequest(params, 'GET', QUERY_KEYS.invoices.AllInvoices, response);
			return response;
		} catch (error: unknown) {
			if (error instanceof ApiError) {
				logApiError(error);
			} else if (error instanceof AxiosError) {
				logAxiosError(error);
			} else {
				logError(error, 'Error fetching all invoices');
			}
			throw error;
		}
	};
	// TODO: this should return InvoiceOptional[] | []
	// should return an empty list instead
	return useQuery<InvoiceOptional[] | undefined, ApiError>(queryKey, query);
};

export const useReadInvoiceNoSql = (params?: ReadSingleInvoiceParams) => {
	const { client } = useApiNoSqlContext();
	const { user } = useAuthenticator(context => [context.user]);
	const queryKey =
		params === undefined
			? [QUERY_KEYS.invoices.ReadInvoice, user]
			: [QUERY_KEYS.invoices.ReadInvoice, params, user];
	return useQuery<Invoice, ApiError>(
		queryKey,
		async () => {
			try {
				if (!client) {
					throw new Error('API client is not initialized');
				}
				if (user.attributes === undefined) {
					throw new Error('AWS Amplify user attributes cannot be undefined');
				}
				if (params !== undefined) {
					return await client.invoice.invoiceReadInvoiceOr404(
						params.id,
						params.managerId,
						params.utilityId,
						params.customerId,
						params.createdDate,
						params.fileName,
						params.invoiceUrl
					);
				}
				else {
					return await client.invoice.invoiceReadInvoiceOr404();
				}
			} catch (error: unknown) {
				if (error instanceof ApiError) {
					logApiError(error);
				} else if (error instanceof AxiosError) {
					logAxiosError(error);
				} else {
					logError(error, 'Error fetching single invoice');
				}
				throw error;
			}
		},
		{
			enabled: !!params?.id
		}
	);
};
/**
 * Custom hook for fetching a single invoice.
 * Endpoint: GET '/api/v2/read/single/invoice'
 * @param params - An object containing the following properties:
 *   - id: The ID of the invoice to fetch (optional).
 *   - utilityId: The utility ID (optional).
 *   - customerId: The customer ID (optional).
 *   - createdDate: The created date (optional).
 *   - fileName: The file name (optional).
 *   - docPredPath: The document predicate path (optional).
 *   - invoiceUrl: The document URL (optional).
 *   - project: Query param string with comma-separated field names as they appear in the database (optional).
 * @returns The query result containing a single InvoiceOptional or an error of type ApiError. The query is only enabled if the 'id' parameter is provided.
 */
export const useReadInvoiceOptionalNoSql = (params?: ReadSingleInvoiceParams) => {
	const { client } = useApiNoSqlContext();
	const { user } = useAuthenticator(context => [context.user]);
	const queryKey =
		params === undefined
			? [QUERY_KEYS.invoices.SingleInvoice, user]
			: [QUERY_KEYS.invoices.SingleInvoice, params, user];
	return useQuery<InvoiceOptional, ApiError>(
		queryKey,
		async () => {
			try {
				if (!client) {
					throw new Error('API client is not initialized');
				}
				if (user.attributes === undefined) {
					throw new Error('AWS Amplify user attributes cannot be undefined');
				}
				const response =
					params === undefined
						? await client.invoice.invoiceReadInvoiceOptionalOr404()
						: await client.invoice.invoiceReadInvoiceOptionalOr404(
							params.id,
							params.utilityId,
							params.customerId,
							params.createdDate,
							params.fileName,
							params.invoiceUrl,
							params.project
						);
				// logRequest(params, 'GET', QUERY_KEYS.invoices.SingleInvoice, response);
				return response;
			} catch (error: unknown) {
				if (error instanceof ApiError) {
					logApiError(error);
				} else if (error instanceof AxiosError) {
					logAxiosError(error);
				} else {
					logError(error, 'Error fetching single invoice');
				}
				throw error;
			}
		},
		{
			enabled: !!params?.id
		}
	);
};

/**
 * Custom hook for fetching a unique field or returning a 404 error.
 * Endpoint: GET '/api/v2/read/unique/{collection}'
 * @param params - An object containing the following properties:
 *   - collection: The name of the collection to query (required).
 *   - unique: The unique field value to search for (optional).
 *   - id: The ID of the invoice (optional).
 *   - documentId: The document ID (optional).
 *   - utilityId: The utility ID (optional).
 *   - customerId: The customer ID (optional).
 *   - updatedDate: The updated date (optional).
 *   - displayName: The display name (optional).
 *   - country: The country (optional).
 *   - stateProvince: The state/province (optional).
 *   - city: The city (optional).
 *   - postalCode: The postal code (optional).
 *   - createdDate: The created date (optional).
 *   - fileName: The file name (optional).
 *   - docPredPath: The document predicate path (optional).
 *   - invoiceUrl: The document URL (optional).
 *   - commodity: The commodity (optional).
 *   - facilityName: The facility name (optional).
 *   - meterStartDate: The meter start date (optional).
 *   - meterEndDate: The meter end date (optional).
 *   - accountNumber: The account number (optional).
 *   - facilityType: The facility type (optional).
 *   - question: The question (optional).
 *   - entity: The entity (optional).
 *   - returnType: The return type (optional).
 *   - isMultiple: Indicates if multiple values are allowed (optional).
 *   - isActive: Indicates if the field is active (optional).
 *   - version: The version number (optional).
 *   - invoiceCategory: The invoice category (optional).
 *   - utilityType: The utility type (optional).
 * @returns any[] or an error of type ApiError. The query is enabled based on the presence of the 'id' parameter.
 */
export const useReadUniqueInvoiceFieldNoSql = (params: ReadUniqueFieldOr404) => {
	const { client } = useApiNoSqlContext();
	const { user } = useAuthenticator(context => [context.user]);

	return useQuery<any[] | undefined, ApiError>(
		[
			QUERY_KEYS.invoices.UniqueFieldOr404,
			params.collection,
			params.unique,
			params.id,
			params.documentId,
			params.utilityId,
			params.customerId,
			params.updatedDate,
			params.createdDate,
			params.fileName,
			params.invoiceUrl,
			params.commodity,
			params.facilityName,
			params.meterStartDate,
			params.meterEndDate,
			params.accountNumber,
			params.facilityType,
			params.question,
			params.entity,
			params.returnType,
			params.isActive,
			params.version,
			params.invoiceCategory,
			params.utilityType,
			user
		],
		async () => {
			try {
				if (!client) {
					throw new Error('API client is not initialized');
				}
				if (user.attributes === undefined) {
					throw new Error('AWS Amplify user attributes cannot be undefined');
				}
				if (params.collection === undefined || params.unique === undefined) {
					throw new Error('Collection name and unique field are required');
				}
				const response = await client.invoice.invoiceReadUniqueFieldOr404(
					params.collection,
					params.unique,
					params.id,
					params.managerId,
					params.documentId,
					params.utilityId,
					params.customerId,
					params.updatedDate,
					params.sqlId,
					params.invoiceCompanyName,
					params.createdDate,
					params.fileName,
					params.invoiceUrl,
          params.isValidated,
					params.commodity,
					params.facilityName,
					params.meterStartDate,
					params.meterEndDate,
					params.accountNumber,
					params.facilityType,
					params.question,
					params.entity,
					params.returnType,
					params.isActive,
					params.version,
					params.invoiceCategory,
					params.utilityType
				);
				// logRequest(params, 'GET', QUERY_KEYS.invoices.UniqueFieldOr404, response);
				return response;
			} catch (error: unknown) {
				if (error instanceof ApiError) {
					logApiError(error);
				} else if (error instanceof AxiosError) {
					logAxiosError(error);
				} else {
					logError(error, 'Error fetching unique field or 404');
				}
				return;
			}
		},
		{
			enabled: !!params.id
		}
	);
};

/**
 * Custom hook for updating an invoice with alerts.
 * Endpoint: PATCH '/api/v2/update-alerts'
 * @returns The mutation result containing the updated invoice or an error of type ApiError.
 */
export const useUpdateInvoiceNoSql = () => {
	const queryClient = useQueryClient();
	const { client } = useApiNoSqlContext();
	const { user } = useAuthenticator(context => [context.user]);

	return useMutation<Invoice, ApiError, Invoice>(
		(params: Invoice) => {
			if (params === undefined) {
				throw new Error(
					'Parameters cannot be undefined and has structure: { requestBody: Invoice }'
				);
			}
			if (!client) {
				throw new Error('API client is not initialized');
			}
			if (user.attributes === undefined) {
				throw new Error('AWS Amplify user attributes cannot be undefined');
			}
			const response = client.invoice.invoiceUpdateInvoiceOr404(params);
			// logRequest(params, 'PATCH', MUTATION_KEYS.invoices.UpdateAlerts, 'alerts updated with patch');
			return response;
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries(QUERY_KEYS.invoices.AllInvoices);
				queryClient.invalidateQueries(QUERY_KEYS.invoices.SingleInvoice);
				queryClient.invalidateQueries(QUERY_KEYS.invoices.ReadInvoice);
				queryClient.invalidateQueries(QUERY_KEYS.invoices.UniqueFieldOr404);
				queryClient.invalidateQueries(QUERY_KEYS.download.SingleBase64File);
			},
			onError: (error: unknown) => {
				if (error instanceof ApiError) {
					logApiError(error);
				} else if (error instanceof AxiosError) {
					logAxiosError(error);
				} else {
					logError(error, 'Error updating invoice');
				}
				return;
			}
		}
	);
};

/**
 * Custom hook for updating an invoice.
 * Endpoint: PATCH '/api/v2/update/invoice/{id}'
 * @returns The mutation result containing the updated invoice or an error of type ApiError.
 */
export const useUpdateInvoiceOptional = () => {
	const queryClient = useQueryClient();
	const { client } = useApiNoSqlContext();
	const { user } = useAuthenticator(context => [context.user]);

	return useMutation<Invoice | undefined, ApiError, UpdateInvoiceParams>(
		(params?: UpdateInvoiceParams) => {
			if (params === undefined) {
				throw new Error(
					'Parameters cannot be undefined and has structure: { id: string, requestBody: InvoiceUpdate }'
				);
			}
			if (!client) {
				throw new Error('API client is not initialized');
			}
			if (user.attributes === undefined) {
				throw new Error('AWS Amplify user attributes cannot be undefined');
			}

			const response = client.invoice.invoiceUpdateInvoiceOptionalOr404(
				params.id,
				params.requestBody
			);
			// logRequest(params, 'PATCH', MUTATION_KEYS.invoices.UpdateInvoice, response);
			return response;
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries(QUERY_KEYS.invoices.AllInvoices);
				queryClient.invalidateQueries(QUERY_KEYS.invoices.SingleInvoice);
				queryClient.invalidateQueries(QUERY_KEYS.invoices.ReadInvoice);
				queryClient.invalidateQueries(QUERY_KEYS.invoices.UniqueFieldOr404);
				queryClient.invalidateQueries(QUERY_KEYS.download.SingleBase64File);
			},
			onError: (error: unknown) => {
				if (error instanceof ApiError) {
					logApiError(error);
				} else if (error instanceof AxiosError) {
					logAxiosError(error);
				} else {
					logError(error, 'Error updating invoice');
				}
				return;
			}
		}
	);
};

export const useSubmitInvoiceToSql = () => {
	const queryClient = useQueryClient();
	const { client } = useApiNoSqlContext();
	const { user } = useAuthenticator(context => [context.user]);

	return useMutation<ReportAPIResponse | undefined, ApiError, { id: string }>(params => {
		if (!client) {
			throw new Error('API client is not initialized');
		}
		if (user.attributes === undefined) {
			throw new Error('AWS Amplify user attributes cannot be undefined');
		}
		const response = client.invoice.invoiceInvoiceToSqlOr404(params.id);
		// logRequest(params, 'PUT', MUTATION_KEYS.invoices.SubmitInvoiceToSql, response);
		return response;
	},
		{
			onSuccess: () => {
				queryClient.invalidateQueries(QUERY_KEYS.invoices.AllInvoices);
				queryClient.invalidateQueries(QUERY_KEYS.invoices.SingleInvoice);
				queryClient.invalidateQueries(QUERY_KEYS.invoices.UniqueFieldOr404);
				queryClient.invalidateQueries(QUERY_KEYS.download.SingleBase64File);
			},
			onError: (error: unknown) => {
				if (error instanceof ApiError) {
					logApiError(error);
				} else if (error instanceof AxiosError) {
					logAxiosError(error);
				} else {
					logError(error, 'Error submitting invoice to SQL');
				}
				return;
			}
		}
	);
};

export const useValidateInvoice = () => {
	const { client } = useApiNoSqlContext();
	const { user } = useAuthenticator(context => [context.user]);
	return useMutation<SQLItem[], ApiError, { id: string }>(
		params => {
			if (!client) {
				throw new Error('API client is not initialized');
			}
			if (user.attributes === undefined) {
				throw new Error('AWS Amplify user attributes cannot be undefined');
			}
			const response = client.invoice.invoiceValidateSqlInvoiceOr404(params.id);
			return response;
		},
		{
			onSuccess: () => {
				console.log('Invoice validated')
			},
			onError: (error: unknown) => {
				if (error instanceof ApiError) {
					logApiError(error);
				} else if (error instanceof AxiosError) {
					logAxiosError(error);
				} else {
					logError(error, 'Error submitting invoice to SQL');
				}
				return;
			}
		}
	);
};